import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchKeyOperationalMetrics } from '../redux/PerformanceReducer/Ecommerce/keyOperationalMetricsSlice';
import { selectIsMultiStoreEnabled, selectUserDetails } from '../redux/UserReducer/userSlice';
import { getEffectiveStoreNumber } from '../utils/appUtils';

/**
 * Custom hook to manage Key Operational Metrics data fetching.
 * Handles:
 * - Fetching data when component mounts
 * - Fetching data when store number or multi-store mode changes
 * - Does NOT retry on error - user must refresh page or navigate away and back
 *
 * This hook performs side effects (dispatching fetch actions) and does not return any value.
 */
export const useKeyOperationalMetrics = (): void => {
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector(selectUserDetails);
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);

  const effectiveStoreNumber = useMemo(
    () => getEffectiveStoreNumber(userDetails, isMultiStoreEnabled),
    [userDetails, isMultiStoreEnabled],
  );

  useEffect(() => {
    if (!effectiveStoreNumber) return;
    dispatch(
      fetchKeyOperationalMetrics({
        storeNumber: effectiveStoreNumber,
        isMultiStoreMode: isMultiStoreEnabled,
      }),
    );
  }, [effectiveStoreNumber, isMultiStoreEnabled, dispatch]);
};
